import { useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  BadgePercent,
  BarChart3,
  BarChartBig,
  Bell,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Crown,
  IndianRupee,
  LandmarkIcon,
  LayoutDashboard,
  ListChecks,
  LucideShoppingCart,
  Menu,
  Power,
  Settings,
  UserCircle,
  Users,
  X,
} from "lucide-react";

import { useGlobalContext } from "../GlobalContext";

import Logo from "../Images/Packky.svg";

import Logo1 from "../Images/P.svg";

import ConfirmLogoutModal from "./Utils/ConfirmLogoutModal";

// Only explicit sidebar controls can change its persisted open/close state.
const SIDEBAR_OPEN_KEY = "packky_admin_sidebar_open";

/* =========================================================
   THEME (orange on white)
   brand      #FC981B  -> icons, accents, active pill
   brand-deep #E8820C  -> hover / active text on light tint
   ink        #9A5400  -> default text (AA contrast on white)
========================================================= */

const EASE = "ease-[cubic-bezier(0.22,1,0.36,1)]";

/* =========================================================
   ICON MAP
========================================================= */

const iconMap = {
  Dashboard: LayoutDashboard,
  Operations: ClipboardList,
  Business: BadgePercent,
  Finance: IndianRupee,
  Reports: BarChartBig,
  Administration: Settings,

  // Submenus
  Tasks: ClipboardList,
  Task: ClipboardList,
  Driver: UserCircle,
  Drivers: UserCircle,
  Teams: Users,
  Team: Users,
  Slots: ListChecks,
  Slot: ListChecks,
  Attendance: CalendarCheck,
  Settings: Settings,
  Setting: Settings,
  Banks: LandmarkIcon,
  Bank: LandmarkIcon,
  Settlement: BarChart3,
  Notification: Bell,
  Notifications: Bell,
  Shops: LucideShoppingCart,
  Shop: LucideShoppingCart,
  Store: LandmarkIcon,
  Admin: Users,
  Tier: Crown,
  Tiers: Crown,
};

const getIcon = (title) => {
  return iconMap?.[title] || LayoutDashboard;
};

const normalizeRoute = (route = "") => {
  const value = String(route || "").trim();

  if (!value) return "";

  return value.startsWith("/") ? value : `/${value}`;
};

const getParentTargetRoute = (item) => {
  if (!item) return "";

  const submenus = Array.isArray(item?.submenus) ? item.submenus : [];

  // If submenu exists, parent click should open the first submenu
  const firstValidSubmenu = submenus.find((submenu) =>
    normalizeRoute(submenu?.submenu_url),
  );

  return (
    normalizeRoute(firstValidSubmenu?.submenu_url) ||
    normalizeRoute(item?.menu_url)
  );
};

/* =========================================================
   USER CARD (module-level so it doesn't remount every render)
========================================================= */

function UserCard({ info, collapsed = false, isOnline = true }) {
  if (!info) return null;

  const name = info?.userName || "Volunteer";

  const role = info?.role || info?.userType || "User";

  const initial = name?.charAt(0)?.toUpperCase() || "V";

  const statusDot = (
    <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
      {isOnline && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none" />
      )}
      <span
        className={`relative inline-flex h-3 w-3 rounded-full ring-2 ring-white ${
          isOnline ? "bg-emerald-500" : "bg-slate-300"
        }`}
      />
    </span>
  );

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 pb-3" title={`${name} · ${role}`}>
        <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#FC981B] to-[#FFB04D] text-sm font-bold text-white shadow-md shadow-orange-200">
          {initial}
          {statusDot}
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <div className="flex items-center gap-3 rounded-2xl border border-[#CDEBF4] bg-[#EAF7FB] p-3">
        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#FC981B] to-[#FFB04D] text-base font-bold text-white shadow-md shadow-orange-200">
          {initial}
          {statusDot}
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-[#0E5468]">
            {name}
          </div>

          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[#1E7A93]">
            <span className="truncate">{role}</span>
            <span className="shrink-0 font-medium text-emerald-600">
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   SIDEBAR
========================================================= */

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const { baseurl, token, setIsUnauthorised, AdminId, logoutAdmin } =
    useGlobalContext();

  const navigate = useNavigate();

  const location = useLocation();

  const [menuItems, setMenuItems] = useState([]);

  const [expandedMenuId, setExpandedMenuId] = useState(null);

  const [volunteerInfo, setVolunteerInfo] = useState(null);

  // Mobile drawer has its own state so it never auto-opens on page load.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Preserve open/closed state even if a parent layout remounts on route change.
  // Navigation itself must never collapse the sidebar.
  useEffect(() => {
    const saved = sessionStorage.getItem(SIDEBAR_OPEN_KEY);
    if (saved === null) {
      sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen));
      return;
    }

    const shouldBeOpen = saved === "true";
    if (sidebarOpen !== shouldBeOpen) {
      setSidebarOpen(shouldBeOpen);
    }
  }, [sidebarOpen, setSidebarOpen]);

  // Use ONLY for direct user actions (desktop toggle button).
  const changeSidebarOpenManually = (nextOpen) => {
    sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(nextOpen));
    setSidebarOpen(nextOpen);
  };

  const navigateKeepingSidebar = (route) => {
    sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen));
    navigate(route);
  };

  const isOnline = true;

  // Lock body scroll + close on Escape while the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileOpen]);

  /* =======================================================
     LOGOUT
  ======================================================= */

  const handleAdminLogout = () => {
    logoutAdmin();

    navigate("/login", {
      replace: true,
    });
  };

  /* =======================================================
     ADMIN INFO
  ======================================================= */

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin");

      if (!raw) return;

      const parsed = JSON.parse(raw);

      setVolunteerInfo(parsed);
    } catch (error) {
      console.error("Failed to parse admin data from session", error);
    }
  }, []);

  /* =======================================================
     GET SIDEBAR ACCESS
  ======================================================= */

  useEffect(() => {
    const fetchAccessMenu = async () => {
      try {
        const res = await fetch(`${baseurl}/api/Admin/v1.0/getUserAccess`, {
          method: "POST",

          headers: {
            Authorization: `Basic ${token}`,

            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            userid: AdminId,
          }),
        });

        const data = await res.json();

        console.log("GET USER ACCESS RESPONSE =>", data);

        if (data?.responseCode === "00") {
          const apiMenus = Array.isArray(data?.response) ? data.response : [];

          const mappedMenus = apiMenus
            .filter(
              (menu) =>
                menu &&
                menu?.status === "Y" &&
                menu?.menu_title &&
                menu?.menu_url,
            )

            .map((menu) => {
              const submenus = Array.isArray(menu?.submenus)
                ? menu.submenus
                    .filter(
                      (submenu) =>
                        submenu &&
                        submenu?.submenu_title &&
                        submenu?.submenu_url,
                    )

                    .map((submenu) => ({
                      id: submenu?.id ?? `${menu?.id}-${submenu?.submenu_url}`,

                      submenu_title: submenu?.submenu_title || "",

                      submenu_icon: submenu?.submenu_icon || "",

                      submenu_url: submenu?.submenu_url || "",
                    }))
                : [];

              return {
                id:
                  menu?.id ??
                  menu?.pk_menu_id ??
                  `${menu?.menu_title}-${menu?.menu_url}`,

                pk_menu_id: menu?.pk_menu_id,

                pk_submenu_id: menu?.pk_submenu_id,

                menu_title: menu?.menu_title || "",

                menu_icon: menu?.menu_icon || "",

                menu_url: menu?.menu_url || "",

                menu_type: menu?.menu_type || "MAIN",

                status: menu?.status || "N",

                submenus,
              };
            });

          console.log("FINAL SIDEBAR MENU =>", mappedMenus);

          setMenuItems(mappedMenus);

          return;
        }

        if (data?.responseCode === "401" || res?.status === 401) {
          setIsUnauthorised(true);
        }
      } catch (error) {
        console.error("Error fetching sidebar access", error);
      }
    };

    if (baseurl && token && AdminId) {
      fetchAccessMenu();
    }
  }, [baseurl, token, AdminId, setIsUnauthorised]);

  /* =======================================================
     ROUTE ACTIVE
  ======================================================= */

  const isRouteActive = (route) => {
    const normalizedRoute = normalizeRoute(route);

    const currentPath = normalizeRoute(location.pathname);

    if (!normalizedRoute || !currentPath) return false;

    // Exact match
    if (currentPath === normalizedRoute) {
      return true;
    }

    // Support nested routes:
    // /operation/tasks/123 -> /operation/tasks
    // /business/store/edit/1 -> /business/store
    if (currentPath.startsWith(`${normalizedRoute}/`)) {
      return true;
    }

    return false;
  };

  /* =======================================================
     CHECK IF SUBMENU SHOULD BE SHOWN
  ======================================================= */

  const getDisplaySubmenus = (item) => {
    const submenus = Array.isArray(item?.submenus) ? item.submenus : [];

    return submenus.filter((submenu) => {
      const parentRoute = normalizeRoute(item?.menu_url);
      const childRoute = normalizeRoute(submenu?.submenu_url);

      // Remove submenu when its route matches the parent route
      return parentRoute !== childRoute;
    });
  };

  const hasDropdown = (item) => {
    return getDisplaySubmenus(item).length > 0;
  };

  /* =======================================================
     PARENT ACTIVE
  ======================================================= */

  const isParentActive = (item) => {
    if (!item) return false;

    const targetRoute = getParentTargetRoute(item);

    // Direct match (parent's own / first-submenu target route)
    if (isRouteActive(targetRoute)) {
      return true;
    }

    // Also active if ANY of its submenus is the active route
    const children = getDisplaySubmenus(item);

    return children.some((submenu) => isRouteActive(submenu?.submenu_url));
  };

  /* =======================================================
     PARENT NAVIGATION
  ======================================================= */

  const handleParentNavigate = (item, closeMobile = false) => {
    if (!item) return;

    const children = getDisplaySubmenus(item);
    const shouldShowDropdown = children.length > 0;

    // Single menu: navigate directly
    if (!shouldShowDropdown) {
      setExpandedMenuId(null);

      const route = normalizeRoute(item?.menu_url);

      if (route) {
        navigateKeepingSidebar(route);

        if (closeMobile) setMobileOpen(false);
      }

      return;
    }

    // Parent has submenus
    const isAlreadyExpanded = String(expandedMenuId) === String(item.id);

    // Clicking an already expanded parent closes the dropdown
    if (isAlreadyExpanded) {
      setExpandedMenuId(null);
      return;
    }

    // Only this parent dropdown stays open
    setExpandedMenuId(item.id);

    // Automatically navigate to first visible submenu
    const firstSubmenuRoute = normalizeRoute(children[0]?.submenu_url);

    if (firstSubmenuRoute) {
      navigateKeepingSidebar(firstSubmenuRoute);
    }
  };

  /* =======================================================
     SUBMENU NAVIGATION
  ======================================================= */

  const handleSubmenuNavigate = (submenu, parentItem, closeMobile = false) => {
    const route = normalizeRoute(submenu?.submenu_url);

    if (!route) {
      console.warn("Submenu route missing:", submenu);

      return;
    }

    // Keep selected parent menu expanded
    if (parentItem?.id != null) {
      setExpandedMenuId(parentItem.id);
    }

    navigateKeepingSidebar(route);

    // Desktop sidebar is never auto-closed; the mobile drawer closes after a pick.
    if (closeMobile) setMobileOpen(false);
  };

  /* =======================================================
     AUTO EXPAND CURRENT NESTED ROUTE
  ======================================================= */

  useEffect(() => {
    if (!menuItems.length) return;

    let activeMenuId = null;

    for (const item of menuItems) {
      const children = getDisplaySubmenus(item);

      const activeChild = children.some((submenu) =>
        isRouteActive(submenu?.submenu_url),
      );

      if (activeChild) {
        activeMenuId = item.id;
        break;
      }
    }

    setExpandedMenuId((previous) =>
      String(previous) === String(activeMenuId) ? previous : activeMenuId,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuItems]);

  /* =======================================================
     SHARED MENU RENDERER (desktop + mobile drawer)
  ======================================================= */

  const renderMenu = ({ collapsed = false, mobile = false }) => (
    <div className="space-y-1 px-3">
      {menuItems.map((item) => {
        const Icon = getIcon(item?.menu_title);

        const children = getDisplaySubmenus(item);

        const hasChildren = hasDropdown(item);

        const expanded = String(expandedMenuId) === String(item.id);

        const parentActive = isParentActive(item);

        const showChildren = hasChildren && expanded && !collapsed;

        return (
          <div key={`${item.id}-${item.menu_url}`}>
            {/* Parent row */}

            <div
              className={`group relative flex h-11 w-full items-center rounded-xl transition-all duration-300 motion-reduce:transition-none ${EASE} ${
                parentActive
                  ? "bg-gradient-to-r from-[#FC981B] to-[#FFB04D] text-white shadow-md shadow-orange-300/50"
                  : "text-[#9A5400] hover:bg-orange-50 hover:text-[#E8820C]"
              }`}
            >
              {/* Parent button: opens submenu list when expanded, navigates otherwise */}

              <button
                type="button"
                title={collapsed ? item?.menu_title : undefined}
                onClick={() => handleParentNavigate(item, mobile)}
                className="flex h-11 min-w-0 flex-1 items-center gap-3 rounded-xl px-[14px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC981B]/60"
              >
                <Icon
                  size={20}
                  className="shrink-0 transition-transform duration-300 group-hover:scale-110 motion-reduce:transform-none"
                />

                <span
                  className={`min-w-0 flex-1 truncate text-sm font-semibold transition-opacity duration-200 ${
                    collapsed ? "opacity-0" : "opacity-100 delay-100"
                  }`}
                >
                  {item?.menu_title}
                </span>
              </button>

              {/* Separate expand arrow */}

              {hasChildren && (
                <button
                  type="button"
                  tabIndex={collapsed ? -1 : 0}
                  onClick={(event) => {
                    event.stopPropagation();

                    handleParentNavigate(item, mobile);
                  }}
                  className={`flex h-8 shrink-0 items-center justify-center rounded-lg transition-all duration-300 ${
                    parentActive ? "hover:bg-white/20" : "hover:bg-orange-100"
                  } ${
                    collapsed
                      ? "pointer-events-none w-0 opacity-0"
                      : "mr-1.5 w-8 opacity-100"
                  }`}
                  aria-label={`Open ${item?.menu_title} submenu`}
                  aria-expanded={expanded}
                >
                  <ChevronRight
                    size={16}
                    className={`transition-transform duration-300 ${EASE} ${
                      expanded ? "rotate-90" : "rotate-0"
                    }`}
                  />
                </button>
              )}
            </div>

            {/* Submenu */}

            {hasChildren && (
              <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none ${
                  showChildren
                    ? "grid-rows-[1fr] opacity-100"
                    : "pointer-events-none grid-rows-[0fr] opacity-0"
                }`}
                aria-hidden={!showChildren}
              >
                <div className="min-h-0 overflow-hidden">
                  <div className="ml-[26px] mt-1 space-y-0.5 border-l-2 border-[#CDEBF4] pb-1 pl-3">
                    {children.map((submenu) => {
                      const SubIcon = getIcon(submenu?.submenu_title);

                      const active = isRouteActive(submenu?.submenu_url);

                      return (
                        <button
                          type="button"
                          tabIndex={showChildren ? 0 : -1}
                          key={`${item.id}-${submenu.id}-${submenu.submenu_url}`}
                          onClick={() =>
                            handleSubmenuNavigate(submenu, item, mobile)
                          }
                          className={`relative flex h-9 w-full items-center gap-2.5 rounded-lg px-3 text-left text-xs transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC981B]/60 ${
                            active
                              ? "bg-[#EAF7FB] font-semibold text-[#1E8FAB]"
                              : "font-medium text-[#9A5400]/80 hover:translate-x-0.5 hover:bg-[#EAF7FB]/80 hover:text-[#1E8FAB] motion-reduce:transform-none"
                          }`}
                        >
                          {active && (
                            <span className="absolute -left-[17px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-[#3BB0CC] ring-2 ring-white" />
                          )}

                          <SubIcon size={15} className="shrink-0" />

                          <span className="truncate">
                            {submenu?.submenu_title}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  /* =======================================================
     LOGOUT BUTTON (shared)
  ======================================================= */

  const renderLogout = ({ collapsed = false }) => (
    <ConfirmLogoutModal
      onConfirm={handleAdminLogout}
      trigger={
        <div
          title={collapsed ? "Logout" : undefined}
          className={`flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-orange-200 bg-white text-[#E8820C] transition-all duration-300 hover:border-red-500 hover:bg-red-500 hover:text-white ${
            collapsed ? "px-0" : "px-3"
          }`}
        >
          <Power size={18} className="shrink-0" />

          <span
            className={`overflow-hidden whitespace-nowrap text-sm font-medium transition-all duration-300 ${
              collapsed ? "max-w-0 opacity-0" : "max-w-[120px] opacity-100"
            }`}
          >
            Logout
          </span>
        </div>
      }
    />
  );

  const avatarInitial =
    volunteerInfo?.userName?.charAt(0)?.toUpperCase() || "V";

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {/* ===================================================
          MOBILE / TABLET TOP BAR
          (page content needs ~56px top padding below lg)
      =================================================== */}

      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-[#D5EEF5] bg-white/95 px-3 backdrop-blur-md lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EAF7FB] text-[#1E8FAB] transition-all duration-200 hover:bg-[#3BB0CC] hover:text-white active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC981B]/60"
          aria-label="Open navigation"
          aria-expanded={mobileOpen}
        >
          <Menu size={20} />
        </button>

        <img className="h-6 w-auto" src={Logo} alt="Packky" />

        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#FC981B] to-[#FFB04D] text-sm font-bold text-white shadow-sm">
          {avatarInitial}
        </div>
      </header>

      {/* ===================================================
          MOBILE / TABLET DRAWER
      =================================================== */}

      <div
        className={`fixed inset-0 z-40 bg-[#0B3A47]/40 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        aria-hidden={!mobileOpen}
        style={{ left: mobileOpen ? 0 : "calc(min(82vw, 300px) * -1)" }}
        className={`fixed inset-y-0 z-50 flex w-[82vw] max-w-[300px] flex-col bg-white transition-[left,box-shadow] duration-300 motion-reduce:transition-none lg:hidden ${EASE} ${
          mobileOpen ? "shadow-2xl shadow-[#0B3A47]/25" : "shadow-none"
        }`}
      >
        {/* Brand accent strip */}
        <div className="h-1 shrink-0 bg-gradient-to-r from-[#FC981B] via-[#FFB04D] to-[#3BB0CC]" />

        {/* Header */}

        <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-[#D5EEF5] px-4">
          <img className="h-7 w-auto" src={Logo} alt="Packky" />

          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FB] text-[#1E8FAB] transition-all duration-200 hover:bg-[#3BB0CC] hover:text-white active:scale-95"
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}

        <nav className="min-h-0 flex-1 overflow-y-auto py-3 [scrollbar-width:thin]">
          {renderMenu({ collapsed: false, mobile: true })}
        </nav>

        {/* Footer */}

        <div className="shrink-0 border-t border-[#D5EEF5] pt-3">
          <UserCard info={volunteerInfo} isOnline={isOnline} />

          <div className="px-3 pb-4">{renderLogout({ collapsed: false })}</div>
        </div>
      </div>

      {/* ===================================================
          DESKTOP SIDEBAR (collapsible)
      =================================================== */}

      <aside
        className={`hidden h-full min-h-0 shrink-0 flex-col overflow-hidden border-r border-[#D5EEF5] bg-white shadow-[4px_0_24px_-12px_rgba(59,176,204,0.35)] transition-[width] duration-300 motion-reduce:transition-none lg:flex ${EASE} ${
          sidebarOpen ? "w-64" : "w-[76px]"
        }`}
      >
        {/* Brand accent strip */}
        <div className="h-1 shrink-0 bg-gradient-to-r from-[#FC981B] via-[#FFB04D] to-[#3BB0CC]" />

        {/* Header */}

        <div className="relative flex h-[76px] min-h-[76px] shrink-0 items-center border-b border-[#D5EEF5] px-3">
          {/* Full logo (open) */}
          <div
            className={`flex min-w-0 flex-1 items-center pl-2 transition-opacity duration-200 ${
              sidebarOpen
                ? "opacity-100 delay-100"
                : "pointer-events-none opacity-0"
            }`}
          >
            <img
              className="h-8 max-w-full object-contain"
              src={Logo}
              alt="Packky"
            />
          </div>

          {/* Toggle: chevron when open, brand mark when collapsed */}
          <button
            type="button"
            onClick={() => changeSidebarOpenManually(!sidebarOpen)}
            className={`group absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC981B]/60 ${EASE} ${
              sidebarOpen
                ? "right-3 bg-[#EAF7FB] text-[#1E8FAB] hover:bg-[#3BB0CC] hover:text-white"
                : "right-[16px] bg-[#EAF7FB] p-1.5 shadow-sm shadow-[#BFE4EF] hover:bg-[#D5EEF5]"
            }`}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? (
              <ChevronLeft size={20} />
            ) : (
              <img
                className="h-full w-full rounded-lg object-contain transition-transform duration-300 group-hover:scale-110 motion-reduce:transform-none"
                src={Logo1}
                alt="Open sidebar"
              />
            )}
          </button>
        </div>

        {/* Navigation */}

        <nav className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-3 [scrollbar-width:thin]">
          {renderMenu({ collapsed: !sidebarOpen, mobile: false })}
        </nav>

        {/* Footer */}

        <div className="shrink-0 border-t border-[#D5EEF5] pt-3">
          <UserCard
            info={volunteerInfo}
            collapsed={!sidebarOpen}
            isOnline={isOnline}
          />

          <div className={sidebarOpen ? "px-3 pb-4" : "px-3 pb-4"}>
            {renderLogout({ collapsed: !sidebarOpen })}
          </div>
        </div>
      </aside>
    </>
  );
}